<script>
import { mapActions, mapGetters } from 'vuex'

// Extract plain text from VNodes (works even when the slot isn't rendered due to an override).
function extractTextFromVNodes(vnodes) {
  const list = Array.isArray(vnodes) ? vnodes : vnodes ? [vnodes] : []

  const walk = (vnode) => {
    if (!vnode) return ''

    // Text node
    if (typeof vnode.text === 'string') return vnode.text

    // <br> support
    if (vnode.tag === 'br') return '\n'

    // Normal element children
    const children =
      vnode.children || (vnode.componentOptions && vnode.componentOptions.children) || []

    return Array.isArray(children) ? children.map(walk).join('') : ''
  }

  return list.map(walk).join('')
}

/**
 * Key-based text component.
 *
 * - Default value is written in the slot (so it's obvious in markup where it goes).
 * - If Firestore has a value for this key, it replaces the default.
 * - When mounted, registers the default text to Vuex (used for initial seeding).
 *
 * Implemented via render() to avoid using v-html on a dynamic <component>,
 * which triggers vue/no-v-text-v-html-on-component.
 */
export default {
  name: 'SiteText',
  props: {
    k: {
      type: String,
      required: true,
    },
    tag: {
      type: String,
      default: 'span',
    },
    html: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters(['SITE_TEXT_VALUE']),
    isHtml() {
      return !!this.html
    },
    resolvedText() {
      const v = this.SITE_TEXT_VALUE(this.k)
      return typeof v === 'string' ? v : ''
    },
    resolvedHtml() {
      const v = this.SITE_TEXT_VALUE(this.k)
      return typeof v === 'string' ? v : ''
    },
    hasOverride() {
      const v = this.SITE_TEXT_VALUE(this.k)
      return typeof v === 'string' && v.length > 0
    },
  },
  methods: {
    ...mapActions(['REGISTER_SITE_TEXT_DEFAULT']),
    registerDefault() {
      if (!this.k) return
      const def = (extractTextFromVNodes(this.$slots.default) || '').trim()
      if (!def) return

      this.REGISTER_SITE_TEXT_DEFAULT({ key: this.k, value: def })
    },
  },
  mounted() {
    // Wait one tick so the slot is rendered, then register defaults.
    this.$nextTick(() => this.registerDefault())
  },
  render(h) {
    const tag = this.tag || 'span'

    // Override from Firestore
    if (this.isHtml && this.hasOverride) {
      return h(tag, { domProps: { innerHTML: this.resolvedHtml } })
    }

    if (!this.isHtml && this.hasOverride) {
      return h(tag, [this.resolvedText])
    }

    // Default slot
    return h(tag, this.$slots.default)
  },
}
</script>
